import Signature from 'react-signature-canvas';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { loader } from 'graphql.macro';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { CF_DOMAIN } from '../constants/aws';
import { useCurrentUser } from '../providers/UserProvider';
import { Text } from './typography';
import Button from './Button';
import styles from './AnnouncementSignaturePad.module.scss';
import { useTranslation } from 'react-i18next';
import { keys } from '../utilities/translator/translation_keys';

const uploadUrlQuery = loader('../graphql/queries/s3.graphql');

const AnnouncementSignaturePad = forwardRef(function AnnouncementSignaturePad(
  { value, onSignedChange = () => {} },
  ref,
) {
  const { t } = useTranslation();
  const client = useApolloClient();
  const { user } = useCurrentUser();
  const signatureRef = useRef();
  const [hasSignature, setHasSignature] = useState(false);

  useImperativeHandle(ref, () => ({
    hasSignature,
    getSignatureUrl: async () => {
      const fileUUID = uuidv4();
      const fileType = 'png';
      const sigCanvas = signatureRef?.current?.getCanvas();

      const {
        data: { simpleStorageUploadUrl: signedUrl },
      } = await client.query({
        query: uploadUrlQuery,
        variables: {
          fileName: `assets/${user.company.id}/${fileUUID}.${fileType}`,
          fileType,
        },
      });

      return new Promise((resolve) => {
        sigCanvas.toBlob((blob) => {
          const file = new File([blob], `${fileUUID}.${fileType}`);
          axios
            .put(signedUrl, file, { headers: { 'Content-Type': fileType } })
            .then((result) => {
              if (result.status === 200) {
                resolve(`${CF_DOMAIN(user)}${fileUUID}.${fileType}`);
              }
            });
        });
      });
    },
  }));

  if (value) {
    return (
      <div className={styles.container}>
        <Text noMargin weight="semiBold">
          {t(keys.announcements.SIGNATURE)}
        </Text>
        <img className={styles.savedSignature} src={value} alt="Signature" />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Text noMargin weight="semiBold">
        {t(keys.announcements.SIGNATURE)}
      </Text>
      <Text noMargin size="sm" color="secondary">
        {t(keys.announcements.SIGNATUREINSTRUCTIONS)}
      </Text>
      <Signature
        penColor="black"
        ref={signatureRef}
        canvasProps={{ className: styles.signature }}
        onBegin={() => {
          setHasSignature(true);
          onSignedChange(true);
        }}
      />
      <Button
        outlined
        size="sm"
        align="right"
        disabled={!hasSignature}
        value={t(keys.action.CLEAR)}
        onClick={() => {
          signatureRef?.current?.clear();
          setHasSignature(false);
          onSignedChange(false);
        }}
      />
    </div>
  );
});

export default AnnouncementSignaturePad;
